/*eslint-disable*/
import React from "react";
import AdminSidebar from "../Sidebar/AdminSidebar";
import AdminNavbar from "../Navbars/AdminNavbar";
import { AxiosResponse } from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RiderApis } from "../../apis/RiderApis";
import { Oval } from "react-loader-spinner";

function FraudDashboard() {
    const [stats, setStats] = React.useState<any>(null);
    const [riskyRiders, setRiskyRiders] = React.useState<any>(null);
    const [disputes, setDisputes] = React.useState<any>(null);
    const [settings, setSettings] = React.useState<any>(null);
    const [loader, setLoader] = React.useState(true);
    const [riderLoader, setRiderLoader] = React.useState(false);
    const [disputeLoader, setDisputeLoader] = React.useState(false);
    const [searchText, setSearchText] = React.useState("");

    // Trust score detail modal
    const [showTrustModal, setShowTrustModal] = React.useState(false);
    const [trustDetail, setTrustDetail] = React.useState<any>(null);
    const [trustLoader, setTrustLoader] = React.useState(false);

    // Active tab
    const [activeTab, setActiveTab] = React.useState<"risky" | "disputes" | "settings">("risky");

    // ─── Load fraud dashboard stats ───────────────────────
    // API Response (flat, no status wrapper):
    // { active_disputes, pending_confirmations, flagged_riders, suspended_wallets,
    //   deliveries_30d, total_pending_balance, ... }
    React.useEffect(() => {
        RiderApis.getFraudDashboard()
            .then((response: AxiosResponse<any>) => {
                // Handle both wrapped { status: true, data: {...} } and flat response
                const data = response?.data?.data || response?.data;
                if (data) {
                    setStats(data);
                }
            })
            .catch((error) => console.log("Dashboard stats error:", error))
            .finally(() => setLoader(false));
    }, []);

    // ─── Load risky riders ────────────────────────────────
    // API Response: { status: true, data: { riders: [...], total, per_page, current_page } }
    React.useEffect(() => {
        fetchRiskyRiders("");
    }, []);

    // ─── Load settings ────────────────────────────────────
    // API Response: { status: true, data: { hold_period_hours, auto_flag_dispute_threshold,
    //   auto_flag_window_days, max_dispute_rate_percent, dispute_spike_threshold_7days,
    //   max_deliveries_per_30min, minimum_withdrawal } }
    React.useEffect(() => {
        RiderApis.getFraudSettings()
            .then((response: AxiosResponse<any>) => {
                const data = response?.data?.data || response?.data;
                if (data) {
                    setSettings(data);
                }
            })
            .catch((error) => console.log("Settings error:", error));
    }, []);

    const fetchRiskyRiders = (page: any) => {
        setRiderLoader(true);
        RiderApis.getRiskyRiders({ page: page || "", per_page: 15, min_deliveries: 1 })
            .then((response: AxiosResponse<any>) => {
                const data = response?.data?.data || response?.data;
                if (data) {
                    setRiskyRiders(data);
                }
            })
            .catch((error) => console.log("Risky riders error:", error))
            .finally(() => setRiderLoader(false));
    };

    const fetchDisputes = (page: any) => {
        setDisputeLoader(true);
        RiderApis.getActiveDisputes({ page: page || "", per_page: 15, search: searchText })
            .then((response: AxiosResponse<any>) => {
                const data = response?.data?.data || response?.data;
                if (data) {
                    setDisputes(data);
                }
            })
            .catch((error) => console.log("Disputes error:", error))
            .finally(() => setDisputeLoader(false));
    };

    // Load disputes when switching to disputes tab
    React.useEffect(() => {
        if (activeTab === "disputes" && !disputes) {
            fetchDisputes("");
        }
    }, [activeTab]);

    // View trust score breakdown
    const viewTrustScore = (userId: number) => {
        setTrustLoader(true);
        setShowTrustModal(true);
        RiderApis.getRiderTrustScore(userId)
            .then((response: AxiosResponse<any>) => {
                const data = response?.data?.data || response?.data;
                if (data) {
                    setTrustDetail(data);
                }
            })
            .catch((error) => toast.error("Failed to load trust score"))
            .finally(() => setTrustLoader(false));
    };

    // ─── Badge / color helpers ────────────────────────────
    const trustColor = (score: number) => {
        if (score >= 80) return "text-green-600";
        if (score >= 60) return "text-yellow-600";
        if (score >= 40) return "text-orange-600";
        return "text-red-600";
    };

    const trustBg = (score: number) => {
        if (score >= 80) return "bg-green-100";
        if (score >= 60) return "bg-yellow-100";
        if (score >= 40) return "bg-orange-100";
        return "bg-red-100";
    };

    const tierBadge = (tier: string) => {
        switch (tier) {
            case "trusted": return "bg-green-100 text-green-700";
            case "reliable": return "bg-blue-100 text-blue-700";
            case "standard": return "bg-yellow-100 text-yellow-700";
            case "probation": return "bg-orange-100 text-orange-700";
            case "suspended": return "bg-red-100 text-red-700";
            default: return "bg-gray-100 text-gray-700";
        }
    };

    // ─── Get riders array from response ───────────────────
    // API returns { riders: [...], total, per_page } — NOT Laravel pagination
    const ridersArray = riskyRiders?.riders || riskyRiders?.data || [];

    // ─── Get disputes array ───────────────────────────────
    const disputesArray = disputes?.data || disputes?.disputes || [];

    if (loader) {
        return (
            <>
                <AdminSidebar title="Fraud Monitor" />
                <div className="relative md:ml-64 bg-[#FAFAFA] min-h-screen flex items-center justify-center">
                    <Oval visible={true} height="50" width="50" color="#E04343" secondaryColor="#FCBF94" ariaLabel="oval-loading" />
                </div>
            </>
        );
    }

    return (
        <>
            <AdminSidebar title="Fraud Monitor" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Fraud Monitoring" />
                <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">

                    {/* ── Stats Overview ────────────────────────────── */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                        <div className="bg-white rounded-[10px] p-4 shadow-sm text-center">
                            <p className="text-[22px] font-[700] text-[#333333]">{riskyRiders?.total || ridersArray.length || 0}</p>
                            <p className="text-[11px] text-[#808080]">Total Riders</p>
                        </div>
                        <div className="bg-red-50 rounded-[10px] p-4 shadow-sm text-center">
                            <p className="text-[22px] font-[700] text-red-600">{stats?.flagged_riders || 0}</p>
                            <p className="text-[11px] text-[#808080]">Flagged</p>
                        </div>
                        <div className="bg-orange-50 rounded-[10px] p-4 shadow-sm text-center">
                            <p className="text-[22px] font-[700] text-orange-600">{stats?.suspended_wallets || 0}</p>
                            <p className="text-[11px] text-[#808080]">Suspended Wallets</p>
                        </div>
                        <div className="bg-yellow-50 rounded-[10px] p-4 shadow-sm text-center">
                            <p className="text-[22px] font-[700] text-yellow-600">{stats?.active_disputes || 0}</p>
                            <p className="text-[11px] text-[#808080]">Active Disputes</p>
                        </div>
                        <div className="bg-blue-50 rounded-[10px] p-4 shadow-sm text-center">
                            <p className="text-[22px] font-[700] text-blue-600">{stats?.pending_confirmations || 0}</p>
                            <p className="text-[11px] text-[#808080]">Pending Confirmations</p>
                        </div>
                        <div className="bg-green-50 rounded-[10px] p-4 shadow-sm text-center">
                            <p className="text-[22px] font-[700] text-green-600">₦{Number(stats?.total_pending_balance || 0).toLocaleString()}</p>
                            <p className="text-[11px] text-[#808080]">Held Funds</p>
                        </div>
                    </div>

                    {/* ── 30-Day Summary (extra detail from API) ────── */}
                    {stats && (stats?.deliveries_30d > 0 || stats?.disputes_30d > 0) && (
                        <div className="bg-white rounded-[10px] p-4 shadow-sm mb-6">
                            <h4 className="text-[13px] font-[600] text-[#333333] mb-2">30-Day Summary</h4>
                            <div className="flex flex-wrap gap-6 text-[12px]">
                                <div>Deliveries: <span className="font-[600]">{stats?.deliveries_30d || 0}</span></div>
                                <div>Disputes: <span className="font-[600] text-red-600">{stats?.disputes_30d || 0}</span></div>
                                <div>Auto-Confirmed: <span className="font-[600]">{stats?.auto_confirmed_30d || 0}</span></div>
                                <div>Receiver Confirmed: <span className="font-[600] text-green-600">{stats?.receiver_confirmed_30d || 0}</span></div>
                                <div>Reversed: <span className="font-[600] text-red-600">₦{Number(stats?.total_reversed_30d || 0).toLocaleString()}</span></div>
                            </div>
                        </div>
                    )}

                    {/* ── Tab Buttons ──────────────────────────────── */}
                    <div className="flex gap-2 mb-6">
                        {[
                            { key: "risky" as const, label: "Risky Riders" },
                            { key: "disputes" as const, label: "Disputes" },
                            { key: "settings" as const, label: "Fraud Settings" },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-4 py-2 rounded-full text-[13px] font-[500] ${activeTab === tab.key
                                        ? "bg-[#E04343] text-white"
                                        : "bg-white text-[#333333] border border-gray-200"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* ═══════════════════════════════════════════════════
              RISKY RIDERS TAB
              ═══════════════════════════════════════════════════ */}
                    {activeTab === "risky" && (
                        <div className="bg-white rounded-[10px] p-6 shadow-sm">
                            <h3 className="text-[15px] font-[600] text-[#333333] mb-4">Riders by Trust Score</h3>

                            {riderLoader ? (
                                <div className="flex justify-center py-10">
                                    <Oval visible={true} height="40" width="40" color="#E04343" secondaryColor="#FCBF94" ariaLabel="oval-loading" />
                                </div>
                            ) : (
                                <div className="relative overflow-x-auto">
                                    <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-3">
                                        <thead className="text-[12px] text-[#030229] bg-[#FAFAFA]">
                                            <tr>
                                                <th className="px-2 py-2">S/N</th>
                                                <th className="px-2 py-2">Rider</th>
                                                <th className="px-2 py-2">Trust Score</th>
                                                <th className="px-2 py-2">Tier</th>
                                                <th className="px-2 py-2">Total Deliveries</th>
                                                <th className="px-2 py-2">Disputes</th>
                                                <th className="px-2 py-2">Dispute Rate</th>
                                                <th className="px-2 py-2">Flagged</th>
                                                <th className="px-2 py-2">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(ridersArray) && ridersArray.length > 0 ? (
                                                ridersArray.map((r: any, index: number) => (
                                                    <tr key={r.rider_id || index} className="bg-white">
                                                        <td className="px-2 py-3 text-[12px]">{index + 1}</td>
                                                        <td className="px-2 py-3">
                                                            <div>
                                                                <p className="text-[12px] font-[500]">{r?.rider_name}</p>
                                                                <p className="text-[10px] text-[#808080]">{r?.email}</p>
                                                            </div>
                                                        </td>
                                                        <td className="px-2 py-3">
                                                            <span className={`text-[14px] font-[700] ${trustColor(r?.trust_score || 0)}`}>
                                                                {r?.trust_score || 0}
                                                            </span>
                                                        </td>
                                                        <td className="px-2 py-3">
                                                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-[500] capitalize ${tierBadge(r?.trust_tier)}`}>
                                                                {r?.trust_tier || "—"}
                                                            </span>
                                                        </td>
                                                        <td className="px-2 py-3 text-[12px] font-[500]">{r?.total_deliveries || 0}</td>
                                                        <td className="px-2 py-3 text-[12px] font-[500] text-red-600">{r?.disputed_deliveries || 0}</td>
                                                        <td className="px-2 py-3 text-[12px] font-[500]">{r?.dispute_rate || 0}%</td>
                                                        <td className="px-2 py-3">
                                                            {r?.is_flagged ? (
                                                                <span className="px-2 py-0.5 rounded-full text-[11px] font-[500] bg-red-100 text-red-700">Flagged</span>
                                                            ) : (
                                                                <span className="text-[11px] text-gray-400">No</span>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-3">
                                                            <button
                                                                onClick={() => viewTrustScore(r.rider_id)}
                                                                className="text-[11px] bg-blue-50 text-blue-600 px-2 py-1 rounded"
                                                            >
                                                                Details
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={9} className="py-8 text-center text-gray-400">No riders found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════
              DISPUTES TAB
              ═══════════════════════════════════════════════════ */}
                    {activeTab === "disputes" && (
                        <div className="bg-white rounded-[10px] p-6 shadow-sm">
                            <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                                <h3 className="text-[15px] font-[600] text-[#333333]">Active Disputes</h3>
                                <div className="flex gap-2 items-center">
                                    <input
                                        type="text"
                                        onChange={(e) => setSearchText(e.target.value)}
                                        placeholder="search by rider or shipment..."
                                        className="border border-gray-300 text-gray-500 text-sm rounded-md pl-4 p-1.5 w-[220px]"
                                    />
                                    <button onClick={() => fetchDisputes("")} className="font-normal text-white bg-[#E04343] px-3 py-1.5 rounded-md text-sm">
                                        Search
                                    </button>
                                </div>
                            </div>

                            {disputeLoader ? (
                                <div className="flex justify-center py-10">
                                    <Oval visible={true} height="40" width="40" color="#E04343" secondaryColor="#FCBF94" ariaLabel="oval-loading" />
                                </div>
                            ) : (
                                <div className="relative overflow-x-auto">
                                    <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-3">
                                        <thead className="text-[12px] text-[#030229] bg-[#FAFAFA]">
                                            <tr>
                                                <th className="px-2 py-2">S/N</th>
                                                <th className="px-2 py-2">Shipment</th>
                                                <th className="px-2 py-2">Rider</th>
                                                <th className="px-2 py-2">Amount</th>
                                                <th className="px-2 py-2">Reason</th>
                                                <th className="px-2 py-2">Disputed At</th>
                                                <th className="px-2 py-2">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(disputesArray) && disputesArray.length > 0 ? (
                                                disputesArray.map((d: any, index: number) => (
                                                    <tr key={d.id || index} className="bg-white">
                                                        <td className="px-2 py-3 text-[12px]">{index + 1}</td>
                                                        <td className="px-2 py-3 text-[12px] font-[500]">{d?.shipment_number}</td>
                                                        <td className="px-2 py-3 text-[12px]">{d?.rider_name || d?.rider?.first_name || "—"}</td>
                                                        <td className="px-2 py-3 text-[12px] font-[500]">₦{Number(d?.amount || d?.wallet_transaction?.amount || 0).toLocaleString()}</td>
                                                        <td className="px-2 py-3 text-[12px] max-w-[200px] truncate">{d?.dispute_reason || "—"}</td>
                                                        <td className="px-2 py-3 text-[12px]">{d?.disputed_at ? new Date(d.disputed_at).toLocaleDateString() : "—"}</td>
                                                        <td className="px-2 py-3">
                                                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-[500] ${d?.resolution_status === "resolved" || d?.status === "confirmed"
                                                                    ? "bg-green-100 text-green-700"
                                                                    : d?.status === "disputed"
                                                                        ? "bg-red-100 text-red-700"
                                                                        : "bg-yellow-100 text-yellow-700"
                                                                }`}>
                                                                {d?.resolution_status || d?.status || "pending"}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={7} className="py-8 text-center text-gray-400">No disputes found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination (if paginated) */}
                            {disputes?.links && (
                                <div className="m-4 flex justify-end mt-10">
                                    {disputes.links.map((datas: any, index: any) => (
                                        <button
                                            key={index}
                                            onClick={() => {
                                                const page = datas?.label === "Next &raquo;"
                                                    ? datas?.url?.charAt(datas?.url?.length - 1)
                                                    : datas?.label === "&laquo; Previous"
                                                        ? datas?.url?.charAt(datas?.url?.length - 1)
                                                        : datas?.label;
                                                fetchDisputes(page);
                                            }}
                                            disabled={datas?.active}
                                            className={"mx-1 py-1 px-2 " + (datas?.active === false || datas?.url === null ? "bg-gray-300 text-black" : "bg-[#E04343] text-white")}
                                        >
                                            {datas?.label === "&laquo; Previous" ? "< Previous" : datas?.label === "Next &raquo;" ? "Next >" : datas?.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════
              SETTINGS TAB
              ═══════════════════════════════════════════════════ */}
                    {activeTab === "settings" && (
                        <div className="bg-white rounded-[10px] p-6 shadow-sm max-w-2xl">
                            <h3 className="text-[16px] font-[600] text-[#333333] mb-4">Fraud Prevention Settings</h3>
                            <p className="text-[12px] text-[#808080] mb-6">These settings are configured via environment variables. Contact your developer to change them.</p>

                            {settings ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="p-3 bg-[#FAFAFA] rounded-[10px]">
                                        <p className="text-[11px] text-[#808080] mb-1">Hold Period (Hours)</p>
                                        <p className="text-[16px] font-[600] text-[#333333]">{settings?.hold_period_hours ?? 48}</p>
                                    </div>
                                    <div className="p-3 bg-[#FAFAFA] rounded-[10px]">
                                        <p className="text-[11px] text-[#808080] mb-1">Auto-Flag After Disputes</p>
                                        <p className="text-[16px] font-[600] text-[#333333]">{settings?.auto_flag_dispute_threshold ?? 3}</p>
                                    </div>
                                    <div className="p-3 bg-[#FAFAFA] rounded-[10px]">
                                        <p className="text-[11px] text-[#808080] mb-1">Auto-Flag Window (Days)</p>
                                        <p className="text-[16px] font-[600] text-[#333333]">{settings?.auto_flag_window_days ?? 30}</p>
                                    </div>
                                    <div className="p-3 bg-[#FAFAFA] rounded-[10px]">
                                        <p className="text-[11px] text-[#808080] mb-1">Max Dispute Rate (%)</p>
                                        <p className="text-[16px] font-[600] text-[#333333]">{settings?.max_dispute_rate_percent ?? 15}%</p>
                                    </div>
                                    <div className="p-3 bg-[#FAFAFA] rounded-[10px]">
                                        <p className="text-[11px] text-[#808080] mb-1">7-Day Dispute Spike Threshold</p>
                                        <p className="text-[16px] font-[600] text-[#333333]">{settings?.dispute_spike_threshold_7days ?? 2}</p>
                                    </div>
                                    <div className="p-3 bg-[#FAFAFA] rounded-[10px]">
                                        <p className="text-[11px] text-[#808080] mb-1">Max Deliveries in 30 Min</p>
                                        <p className="text-[16px] font-[600] text-[#333333]">{settings?.max_deliveries_per_30min ?? 5}</p>
                                    </div>
                                    <div className="p-3 bg-[#FAFAFA] rounded-[10px]">
                                        <p className="text-[11px] text-[#808080] mb-1">Min Withdrawal (₦)</p>
                                        <p className="text-[16px] font-[600] text-[#333333]">₦{Number(settings?.minimum_withdrawal ?? 500).toLocaleString()}</p>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-center text-gray-400 py-4">Settings not available</p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════
          TRUST SCORE MODAL
          ═══════════════════════════════════════════════════════ */}
            {showTrustModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-[10px] max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-[16px] font-[600] text-[#333333]">Trust Score Breakdown</h3>
                            <button onClick={() => { setShowTrustModal(false); setTrustDetail(null); }} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                        </div>

                        {trustLoader ? (
                            <div className="flex justify-center py-8">
                                <Oval visible={true} height="40" width="40" color="#E04343" secondaryColor="#FCBF94" ariaLabel="oval-loading" />
                            </div>
                        ) : trustDetail ? (
                            <div>
                                {/* Score Circle */}
                                {/* API: trust_score = { score: 40, tier: "probation", factors: [...] } */}
                                <div className="text-center mb-6">
                                    <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${trustBg(trustDetail?.trust_score?.score || 0)}`}>
                                        <span className={`text-[28px] font-[700] ${trustColor(trustDetail?.trust_score?.score || 0)}`}>
                                            {trustDetail?.trust_score?.score || 0}
                                        </span>
                                    </div>
                                    <p className="text-[13px] text-[#808080] mt-2">{trustDetail?.rider?.name || "—"}</p>
                                    {trustDetail?.trust_score?.tier && (
                                        <span className={`mt-1 inline-block px-3 py-0.5 rounded-full text-[11px] font-[500] capitalize ${tierBadge(trustDetail.trust_score.tier)}`}>
                                            {trustDetail.trust_score.tier}
                                        </span>
                                    )}
                                </div>

                                {/* Trust Score Factors */}
                                {/* API: trust_score.factors = [{ factor: "KYC all fields match", impact: "+20" }, ...] */}
                                {Array.isArray(trustDetail?.trust_score?.factors) && trustDetail.trust_score.factors.length > 0 && (
                                    <div className="space-y-2 mb-4">
                                        <h4 className="text-[12px] font-[600] text-[#333333]">Score Factors</h4>
                                        {trustDetail.trust_score.factors.map((f: any, i: number) => (
                                            <div key={i} className="flex justify-between items-center p-2 bg-[#FAFAFA] rounded-[8px]">
                                                <span className="text-[12px] text-[#333333]">{f?.factor || "—"}</span>
                                                <span className={`text-[13px] font-[600] ${String(f?.impact).startsWith('+') ? "text-green-600" : String(f?.impact).startsWith('-') ? "text-red-600" : "text-gray-500"}`}>
                                                    {f?.impact || "0"}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Stats */}
                                {/* API: stats = { total_deliveries, successful_deliveries, disputed_deliveries, dispute_rate, is_flagged, flag_reason, account_age_days } */}
                                <div className="p-3 bg-[#FAFAFA] rounded-[10px] text-[12px]">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>Total Deliveries: <span className="font-[600]">{trustDetail?.stats?.total_deliveries || 0}</span></div>
                                        <div>Successful: <span className="font-[600] text-green-600">{trustDetail?.stats?.successful_deliveries || 0}</span></div>
                                        <div>Disputed: <span className="font-[600] text-red-600">{trustDetail?.stats?.disputed_deliveries || 0}</span></div>
                                        <div>Failed: <span className="font-[600] text-orange-600">{trustDetail?.stats?.failed_deliveries || 0}</span></div>
                                        <div>Dispute Rate: <span className="font-[600]">{typeof trustDetail?.stats?.dispute_rate === 'number' ? trustDetail.stats.dispute_rate.toFixed(1) : (trustDetail?.stats?.dispute_rate || 0)}%</span></div>
                                        <div>Account Age: <span className="font-[600]">{trustDetail?.stats?.account_age_days || 0} days</span></div>
                                        <div>Flagged: <span className={`font-[600] ${trustDetail?.stats?.is_flagged ? "text-red-600" : "text-green-600"}`}>{trustDetail?.stats?.is_flagged ? "Yes" : "No"}</span></div>
                                        {trustDetail?.stats?.flag_reason && <div className="col-span-2">Reason: <span className="font-[500] text-red-600">{trustDetail.stats.flag_reason}</span></div>}
                                    </div>
                                </div>

                                {/* Recent Disputes */}
                                {Array.isArray(trustDetail?.recent_disputes) && trustDetail.recent_disputes.length > 0 && (
                                    <div className="mt-4">
                                        <h4 className="text-[12px] font-[600] text-[#333333] mb-2">Recent Disputes</h4>
                                        {trustDetail.recent_disputes.map((d: any, i: number) => (
                                            <div key={d.id || i} className="flex justify-between items-center p-2 bg-red-50 rounded-[8px] mb-1 text-[11px]">
                                                <span className="font-[500]">{d.shipment_number}</span>
                                                <span className="text-[#808080] max-w-[150px] truncate">{d.dispute_reason || "—"}</span>
                                                <span className="text-[#808080]">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Recent Confirmations */}
                                {Array.isArray(trustDetail?.recent_confirmations) && trustDetail.recent_confirmations.length > 0 && (
                                    <div className="mt-4">
                                        <h4 className="text-[12px] font-[600] text-[#333333] mb-2">Recent Confirmations</h4>
                                        {trustDetail.recent_confirmations.map((c: any, i: number) => (
                                            <div key={c.id || i} className="flex justify-between items-center p-2 bg-green-50 rounded-[8px] mb-1 text-[11px]">
                                                <span className="font-[500]">{c.shipment_number}</span>
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] ${c.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{c.status}</span>
                                                <span className="text-[#808080]">{c.confirmation_method || "—"}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-center text-gray-400 py-4">No data available</p>
                        )}

                        <div className="flex justify-end mt-4">
                            <button onClick={() => { setShowTrustModal(false); setTrustDetail(null); }} className="border border-gray-300 text-[13px] px-4 py-2 rounded-[10px]">Close</button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar={true} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </>
    );
}

export default FraudDashboard;